import React, { useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Avatar,
  Chip,
  Collapse,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector } from "react-redux";
import { CompanyFormData, RepresentativeData } from "../../types/company-types";
import { RootState } from "../../../../store/Index";
import { FieldLabel, StyledTextField } from "../../types/company-form";
import { MuiTelInputInfo } from "mui-tel-input";
import apiService from "../../../../service/Api";
import RepFormFields, { RepFormValues } from "../RepFormFields";

interface Step3RepresentativesProps {
  formData: CompanyFormData;
  setFormData: React.Dispatch<React.SetStateAction<CompanyFormData>>;
}

interface ContactSearchResult {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string | null;
  contact_image: string | null;
}
type Mode = "idle" | "search" | "create";

function generateId() {
  return Math.random().toString(36).slice(2, 9);
}

const EMPTY_FORM: RepFormValues = {
  firstName: "",
  lastName: "",
  role: "",
  phone: "",
  email: "",
};

function getInitials(firstName: string, lastName: string) {
  const parts = [firstName, lastName].filter(Boolean);
  if (parts.length === 0) return "R";
  return parts
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function debounce<T extends (...args: string[]) => void>(fn: T, delay: number) {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

const avatarColor = (id: string) => {
  const colors = [
    "#7C3AED",
    "#2563EB",
    "#059669",
    "#D97706",
    "#DC2626",
    "#0891B2",
  ];
  const idx = id.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return colors[idx % colors.length];
};

export default function Representatives({
  formData,
  setFormData,
}: Step3RepresentativesProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color400 = reduxTheme.primary_colors?.[400] ?? "#8B4513";
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const colorPrimary = color400;

  const reps = useMemo(
    () => formData.representatives ?? [],
    [formData.representatives],
  );

  const [mode, setMode] = useState<Mode>("idle");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ContactSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [pendingContact, setPendingContact] =
    useState<ContactSearchResult | null>(null);
  const [createForm, setCreateForm] = useState<RepFormValues>(EMPTY_FORM);
  const [editingRepId, setEditingRepId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<RepFormValues>(EMPTY_FORM);

  const [createPhoto, setCreatePhoto] = useState<File | null>(null);
  const createPhotoInputRef = useRef<HTMLInputElement>(null);
  const debouncedSearch = React.useRef(
    debounce(async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }
      setSearching(true);
      try {
        const res = await apiService.get<{ results: ContactSearchResult[] }>(
          `api/contacts/contact/?search=${encodeURIComponent(query)}`,
        );
        setSearchResults(res.data.results ?? []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 500),
  ).current;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    debouncedSearch(e.target.value);
  };

  // ✅ Correct — sets pending, waits for confirm
  const handleSelectExisting = (contact: ContactSearchResult) => {
    const alreadyAdded = reps.some((r) => r.existingContactId === contact.id);
    if (alreadyAdded) return;
    setPendingContact(contact);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleCreateFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleConfirmPending = () => {
    if (!pendingContact) return;
    const rep: RepresentativeData = {
      id: generateId(),
      firstName: pendingContact.first_name ?? "",
      lastName: pendingContact.last_name ?? "",
      role: pendingContact.role ?? "",
      phone: pendingContact.phone ?? "",
      email: pendingContact.email ?? "",
      existingContactId: pendingContact.id,
    };
    setFormData((prev) => ({
      ...prev,
      representatives: [...prev.representatives, rep],
    }));
    setPendingContact(null);
    setMode("idle");
  };
  const handleCreatePhoneChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setCreateForm((prev) => ({ ...prev, phone: value }));
  };

  const handleCreateRoleChange = (role: string) => {
    setCreateForm((prev) => ({ ...prev, role }));
  };

  const handleSaveCreate = () => {
    if (!createForm.firstName.trim()) return;
    const rep: RepresentativeData = {
      id: generateId(),
      ...createForm,
      photoFile: createPhoto,
    };
    setFormData((prev) => ({
      ...prev,
      representatives: [...prev.representatives, rep],
    }));
    setCreateForm(EMPTY_FORM);
    setCreatePhoto(null);
    setMode("idle");
  };

  const openEdit = (rep: RepresentativeData) => {
    setEditForm({
      firstName: rep.firstName,
      lastName: rep.lastName,
      role: rep.role,
      phone: rep.phone,
      email: rep.email,
    });
    setEditingRepId(rep.id);
  };

  const cancelEdit = () => {
    setEditingRepId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditPhoneChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setEditForm((prev) => ({ ...prev, phone: value }));
  };

  const handleEditRoleChange = (role: string) => {
    setEditForm((prev) => ({ ...prev, role }));
  };

  const saveEdit = () => {
    if (!editingRepId) return;
    setFormData((prev) => ({
      ...prev,
      representatives: prev.representatives.map((r) =>
        r.id === editingRepId ? { ...r, ...editForm } : r,
      ),
    }));
    cancelEdit();
  };

  const removeRep = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      representatives: prev.representatives.filter((r) => r.id !== id),
    }));
    if (editingRepId === id) cancelEdit();
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Representatives
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 3, display: "block" }}
      >
        Search existing contacts or create new ones for this company.
      </Typography>

      {/* ── Entry panel ── */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: alpha(color300 ?? "#ccc", 0.3),
          borderRadius: "16px",
          p: 2.5,
          mb: 3,
          backgroundColor: alpha(color800 ?? "#f5f5f5", 0.4),
        }}
      >
        {/* Mode toggle */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: mode !== "idle" ? 2.5 : 0,
            p: 1.5,
            borderRadius: "14px",
            backgroundColor: alpha(color300 ?? "#ccc", 0.08),
            border: "1px solid",
            borderColor: alpha(color300 ?? "#ccc", 0.15),
            width: "fit-content",
          }}
        >
          {[
            {
              m: "search" as Mode,
              icon: <SearchIcon sx={{ fontSize: 20 }} />,
              label: "Search Existing",
            },
            {
              m: "create" as Mode,
              icon: <PersonAddAlt1Icon sx={{ fontSize: 20 }} />,
              label: "Create New",
            },
          ].map(({ m, icon, label }) => (
            <Box
              key={m}
              onClick={() => setMode((prev) => (prev === m ? "idle" : m))}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.6,
                cursor: "pointer",
                opacity: mode !== "idle" && mode !== m ? 0.45 : 1,
                transition: "opacity 0.2s ease",
                minWidth: 80,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor:
                    mode === m ? colorPrimary : alpha(color300 ?? "#ccc", 0.15),
                  color: mode === m ? "#fff" : color100,
                  border: "1.5px solid",
                  borderColor:
                    mode === m ? colorPrimary : alpha(color300 ?? "#ccc", 0.25),
                  transition: "all 0.2s ease",
                  boxShadow:
                    mode === m
                      ? `0 4px 12px ${alpha(colorPrimary ?? "#ccc", 0.35)}`
                      : "none",
                }}
              >
                {icon}
              </Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: mode === m ? color100 : color300,
                  letterSpacing: "0.02em",
                  userSelect: "none",
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Search panel */}
        <Collapse in={mode === "search"}>
          <FieldLabel textcolor={color100}>
            Search by name, email or phone
          </FieldLabel>
          <StyledTextField
            placeholder="Search existing contacts..."
            value={searchQuery}
            onChange={handleSearchChange}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
            InputProps={{
              startAdornment: searching ? (
                <CircularProgress size={14} sx={{ mr: 1 }} />
              ) : (
                <SearchIcon
                  sx={{ fontSize: 16, color: "text.disabled", mr: 1 }}
                />
              ),
            }}
          />

          {/* Results list — only show when no pending contact */}
          {!pendingContact && searchResults.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              {searchResults.map((contact) => {
                const alreadyAdded = reps.some(
                  (r) => r.existingContactId === contact.id,
                );
                return (
                  <Box
                    key={contact.id}
                    onClick={() =>
                      !alreadyAdded && handleSelectExisting(contact)
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.25,
                      borderRadius: "12px",
                      cursor: alreadyAdded ? "not-allowed" : "pointer",
                      border: "1px solid",
                      borderColor: alpha(color300 ?? "#ccc", 0.2),
                      opacity: alreadyAdded ? 0.5 : 1,
                      "&:hover": {
                        bgcolor: alreadyAdded
                          ? "transparent"
                          : alpha(colorPrimary ?? "#ccc", 0.06),
                      },
                    }}
                  >
                    <Avatar
                      src={contact.contact_image ?? undefined}
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: 13,
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(
                        contact.first_name ?? "",
                        contact.last_name ?? "",
                      )}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: color100 }}
                      >
                        {[contact.first_name, contact.last_name]
                          .filter(Boolean)
                          .join(" ")}
                      </Typography>
                      {contact.email && (
                        <Typography
                          variant="caption"
                          sx={{ color: color300, display: "block" }}
                        >
                          {contact.email}
                        </Typography>
                      )}
                    </Box>
                    {alreadyAdded && (
                      <Chip
                        label="Added"
                        size="small"
                        sx={{
                          fontSize: "10px",
                          height: 18,
                          color: color300,
                          borderColor: color300,
                        }}
                        variant="outlined"
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {searchQuery.trim() &&
            !searching &&
            searchResults.length === 0 &&
            !pendingContact && (
              <Typography
                variant="caption"
                sx={{ color: color300, mt: 1, display: "block" }}
              >
                No contacts found. Try Create New instead.
              </Typography>
            )}

          {/* Pending contact confirmation */}
          {pendingContact && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: alpha(colorPrimary ?? "#ccc", 0.35),
                bgcolor: alpha(colorPrimary ?? "#ccc", 0.04),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    src={pendingContact.contact_image ?? undefined}
                    sx={{ width: 28, height: 28, fontSize: 11 }}
                  >
                    {getInitials(
                      pendingContact.first_name ?? "",
                      pendingContact.last_name ?? "",
                    )}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: color100 }}
                    >
                      {[pendingContact.first_name, pendingContact.last_name]
                        .filter(Boolean)
                        .join(" ")}
                    </Typography>
                    {pendingContact.email && (
                      <Typography variant="caption" sx={{ color: color300 }}>
                        {pendingContact.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setPendingContact(null)}
                  sx={{ color: color300 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mt: 1.5,
                }}
              >
                <Button
                  onClick={() => setPendingContact(null)}
                  sx={{
                    textTransform: "none",
                    fontSize: "13px",
                    color: color300,
                    borderRadius: "10px",
                    px: 2,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmPending}
                  variant="contained"
                  disableElevation
                  startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
                  sx={{
                    textTransform: "none",
                    fontSize: "13px",
                    fontWeight: 600,
                    borderRadius: "10px",
                    px: 2.5,
                    backgroundColor: colorPrimary,
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: alpha(colorPrimary ?? "#ccc", 0.85),
                    },
                  }}
                >
                  Add Representative
                </Button>
              </Box>
            </Box>
          )}
        </Collapse>

        {/* Create panel */}
        {/* Create panel */}
        <Collapse in={mode === "create"}>
          {/* ── Photo upload ── */}
          <Box
            sx={{
              mb: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <Avatar
              src={createPhoto ? URL.createObjectURL(createPhoto) : undefined}
              sx={{
                width: 75,
                height: 75,
                bgcolor: createPhoto ? undefined : colorPrimary,
                fontSize: 15,
                fontWeight: 700,
              }}
            >
              {createForm.firstName
                ? createForm.firstName[0]?.toUpperCase()
                : "?"}
            </Avatar>
            <input
              ref={createPhotoInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => setCreatePhoto(e.target.files?.[0] ?? null)}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => createPhotoInputRef.current?.click()}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontSize: 12,
                borderColor: colorPrimary,
                color: colorPrimary,
              }}
            >
              {createPhoto ? "Change Photo" : "Upload Photo"}
            </Button>
            {createPhoto && (
              <IconButton
                size="small"
                onClick={() => setCreatePhoto(null)}
                sx={{ color: color300 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>

          <RepFormFields
            values={createForm}
            onChange={handleCreateFormChange}
            onPhoneChange={handleCreatePhoneChange}
            onRoleChange={handleCreateRoleChange}
            color100={color100}
            color300={color300}
            color700={color700}
            color800={color800}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 2.5,
            }}
          >
            <Button
              onClick={() => setMode("idle")}
              startIcon={<CloseIcon sx={{ fontSize: "15px !important" }} />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "13px",
                color: color300,
                px: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveCreate}
              disabled={!createForm.firstName.trim()}
              disableElevation
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 600,
                backgroundColor: colorPrimary,
                color: "#fff",
                px: 2.5,
                "&:hover": {
                  backgroundColor: alpha(colorPrimary ?? "#ccc", 0.85),
                },
                "&.Mui-disabled": {
                  backgroundColor: color800,
                  color: color300,
                },
              }}
            >
              Add Representative
            </Button>
          </Box>
        </Collapse>
      </Box>

      {/* ── Rep cards ── */}
      {reps.length > 0 && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: color300,
              }}
            >
              Added Representatives
            </Typography>
            <Chip
              label={reps.length}
              size="small"
              sx={{
                height: 18,
                fontSize: "11px",
                fontWeight: 700,
                backgroundColor: alpha(colorPrimary ?? "#ccc", 0.15),
                color: colorPrimary,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {reps.map((rep, index) => (
              <Box key={rep.id}>
                {/* Edit form */}
                <Collapse in={editingRepId === rep.id}>
                  <Box
                    sx={{
                      border: "1.5px solid",
                      borderColor: alpha(colorPrimary ?? "#ccc", 0.35),
                      borderRadius: "14px",
                      p: 2.5,
                      mb: 1,
                      backgroundColor: alpha(color800 ?? "#f5f5f5", 0.6),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: color100,
                        }}
                      >
                        Editing:{" "}
                        {[rep.firstName, rep.lastName]
                          .filter(Boolean)
                          .join(" ") || `Rep ${index + 1}`}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={cancelEdit}
                        sx={{ color: color300 }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>

                    <RepFormFields
                      values={editForm}
                      onChange={handleEditFormChange}
                      onPhoneChange={handleEditPhoneChange}
                      onRoleChange={handleEditRoleChange}
                      color100={color100}
                      color300={color300}
                      color700={color700}
                      color800={color800}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 2.5,
                      }}
                    >
                      <Button
                        onClick={cancelEdit}
                        sx={{
                          borderRadius: "10px",
                          textTransform: "none",
                          fontSize: "13px",
                          color: color300,
                          px: 2,
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={saveEdit}
                        disabled={!editForm.firstName.trim()}
                        disableElevation
                        variant="contained"
                        sx={{
                          borderRadius: "10px",
                          textTransform: "none",
                          fontSize: "13px",
                          fontWeight: 600,
                          backgroundColor: colorPrimary,
                          color: "#fff",
                          px: 2.5,
                          "&:hover": {
                            backgroundColor: alpha(
                              colorPrimary ?? "#ccc",
                              0.85,
                            ),
                          },
                          "&.Mui-disabled": {
                            backgroundColor: color800,
                            color: color300,
                          },
                        }}
                      >
                        Save Changes
                      </Button>
                    </Box>
                  </Box>
                </Collapse>

                {/* Card */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: "14px",
                    border: "1px solid",
                    borderColor:
                      editingRepId === rep.id
                        ? alpha(colorPrimary ?? "#ccc", 0.4)
                        : alpha(color300 ?? "#ccc", 0.2),
                    backgroundColor:
                      editingRepId === rep.id
                        ? alpha(colorPrimary ?? "#ccc", 0.04)
                        : alpha(color800 ?? "#f5f5f5", 0.5),
                    transition: "all 0.2s ease",
                    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      backgroundColor: avatarColor(rep.id),
                      fontSize: "15px",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(rep.firstName, rep.lastName)}
                  </Avatar>

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: color100,
                        lineHeight: 1.3,
                      }}
                    >
                      {[rep.firstName, rep.lastName]
                        .filter(Boolean)
                        .join(" ") || `Rep ${index + 1}`}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        mt: 0.5,
                      }}
                    >
                      {rep.role && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                          }}
                        >
                          <WorkOutlineIcon
                            sx={{ fontSize: 12, color: color300 }}
                          />
                          <Typography
                            sx={{ fontSize: "12px", color: color300 }}
                          >
                            {rep.role}
                          </Typography>
                        </Box>
                      )}
                      {rep.email && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                          }}
                        >
                          <EmailOutlinedIcon
                            sx={{ fontSize: 12, color: color300 }}
                          />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: color300,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 160,
                            }}
                          >
                            {rep.email}
                          </Typography>
                        </Box>
                      )}
                      {rep.phone && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                          }}
                        >
                          <PhoneOutlinedIcon
                            sx={{ fontSize: 12, color: color300 }}
                          />
                          <Typography
                            sx={{ fontSize: "12px", color: color300 }}
                          >
                            {rep.phone}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => openEdit(rep)}
                      sx={{
                        color:
                          editingRepId === rep.id ? colorPrimary : color300,
                        opacity: 0.8,
                        "&:hover": {
                          opacity: 1,
                          backgroundColor: alpha(colorPrimary ?? "#ccc", 0.08),
                        },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => removeRep(rep.id)}
                      sx={{
                        color: "error.light",
                        opacity: 0.7,
                        "&:hover": {
                          opacity: 1,
                          backgroundColor: alpha("#f44336", 0.08),
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
